import React from 'react';
import Field from './Field';

const PROCESSING = 'Processing...';

const storageUrl = (filename) =>
  `https://console.cloud.google.com/storage/browser/default-websites-load-balancer-bucket/images?inv=1&invt=Ab2wYQ&project=hosting-for-digital-projects&pageState=(%22StorageObjectListTable%22:(%22f%22:%22%255B%257B_22k_22_3A_22_22_2C_22t_22_3A10_2C_22v_22_3A_22_5C_22${encodeURIComponent(filename)}_5C_22_22%257D%255D%22))&prefix=${encodeURIComponent(filename)}&forceOnObjectsSortingFiltering=false`;

/**
 * Main image picker + metadata. The 'Processing...' filename is the wire
 * contract that tells the backend to copy the media into the website bucket
 * on the next page save; it is set by the container's onSelect and only
 * displayed here.
 */
export default function MainImageCard({ page, isNewPage, mainImageUrl, imageError, onChange, onPick, onRemove, onClear }) {
  const img = page.main_image || {};
  const hasTopic = !!(page.primary_topic || '').trim();
  const hasImage = !!(img.img_filename || img.img_original_filename);
  const processing = img.img_filename === PROCESSING;

  return (
    <section className="card" aria-labelledby="main-image-title">
      <div className="card-header">
        <h3 className="card-title" id="main-image-title">Main image</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onPick}
            disabled={!hasTopic}
            title={hasTopic ? 'Choose an image from the library' : 'Set the primary topic (Details card) first'}
          >
            {hasImage ? 'Change image' : 'Select image'}
          </button>
          {hasImage && !isNewPage && (
            <button type="button" className="btn btn-danger btn-sm" onClick={onRemove}>
              Remove
            </button>
          )}
          {hasImage && isNewPage && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClear}>
              Clear selection
            </button>
          )}
        </div>
      </div>

      {imageError && <div className="alert alert-error" role="alert">{imageError}</div>}

      {!hasTopic && (
        <p className="hint" style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-muted)' }}>
          Set the <strong>primary topic</strong> in the Details card before choosing an image — it names the image file.
        </p>
      )}

      {mainImageUrl && (
        <div style={{ marginBottom: 12, textAlign: 'center' }}>
          <img src={mainImageUrl} alt={img.img_alt || 'Main image preview'} style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 'var(--radius)' }} />
        </div>
      )}

      {hasImage && (
        <p style={{ margin: '0 0 12px', fontSize: 13 }}>
          {processing ? (
            <>
              Selected: {img.img_original_filename}{' '}
              <span style={{ color: 'var(--color-warning)', fontStyle: 'italic' }}>— processes when you save (renamed to {'{topic}_{media_id}'})</span>
            </>
          ) : img.img_filename ? (
            <>
              File: <code>{img.img_filename}</code>{' '}
              <a href={storageUrl(img.img_filename)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Cloud Storage ↗</a>
            </>
          ) : (
            <>Selected: {img.img_original_filename} (awaiting processing)</>
          )}
        </p>
      )}

      {hasImage && (
        <>
          <div className="field-row">
            <Field id="main-image-alt" label="Alt text">
              <input id="main-image-alt" type="text" className="input" value={img.img_alt || ''} onChange={(e) => onChange('main_image.img_alt', e.target.value)} placeholder="Describe the image" />
            </Field>
            <Field id="main-image-caption" label="Caption">
              <input id="main-image-caption" type="text" className="input" value={img.img_caption || ''} onChange={(e) => onChange('main_image.img_caption', e.target.value)} placeholder="Shown below the image" />
            </Field>
          </div>
          <div className="field-row">
            <Field id="main-image-source" label="Source">
              <input id="main-image-source" type="text" className="input" value={img.source || ''} onChange={(e) => onChange('main_image.source', e.target.value)} placeholder="e.g. Unsplash" />
            </Field>
            <Field id="main-image-author" label="Source author">
              <input id="main-image-author" type="text" className="input" value={img.source_author || ''} onChange={(e) => onChange('main_image.source_author', e.target.value)} placeholder="e.g. Jane Smith" />
            </Field>
          </div>
          <Field id="main-image-media-id" label="Media ID" hint="Set automatically from the library.">
            <input id="main-image-media-id" type="text" className="input mono" value={img.media_id || ''} readOnly />
          </Field>
        </>
      )}
    </section>
  );
}
